import sanitizeHtml from "sanitize-html";

const zeroes = "0px 0px 0px 0px";

/**
 * `inlineStylesForDeliver` inlines a set of known
 * important styles for delivery by email.
 * It runs client-side because a DOM is easily available
 * in the browser, and is more difficult to load on the server.
 * @param body  User supplied email body.
 * @returns     Email body as string with styles inlined.
 */
export function inlineStylesForDeliver(body: string): string {
    const wrapper = document.createElement("div");
    wrapper.innerHTML = body;
    Array.from(wrapper.getElementsByTagName("p")).forEach(p => {
        p.style.padding = zeroes;
        p.style.margin = zeroes;
    });
    return wrapper.innerHTML;
}

interface SanitizedMarkUp {
    __html: string;
}

export const getSanitizedMarkUpFromEmailBody = (htmlBody: string): SanitizedMarkUp => {
    const parser = new DOMParser();
    const parsedHtml = parser.parseFromString(htmlBody, "text/html");
    const dirtyHtml = parsedHtml.body.innerHTML;

    const colorCodeRegexMatchers = {
        hex: /^#(0x)?[0-9a-f]+$/i,
        rgb: /^rgb\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})\s*\)$/,
        name: /^(blue|violet|orange|gray)$/,
    };

    const cleanHtml = sanitizeHtml(dirtyHtml, {
        allowedTags: [
            "br",
            "u",
            "b",
            "i",
            "em",
            "strong",
            "a",
            "img",
            "p",
            "span",
            "div",
            "h1",
            "h2",
            "ul",
            "ol",
            "li",
        ],
        allowedAttributes: {
            a: ["href", "target"],
            img: ["src", "alt", "width", "height"],
            p: ["style"],
            span: ["style"],
            div: ["style"],
        },
        allowedSchemesByTag: {
            img: ["https"],
        },
        allowedStyles: {
            "*": {
                // Match HEX and RGB and ColorNames
                color: [
                    colorCodeRegexMatchers.hex,
                    colorCodeRegexMatchers.rgb,
                    colorCodeRegexMatchers.name,
                ],
                "background-color": [
                    colorCodeRegexMatchers.hex,
                    colorCodeRegexMatchers.rgb,
                    colorCodeRegexMatchers.name,
                ],
                "text-align": [/^left$/, /^right$/, /^center$/],
                "font-family": [/^[a-z0-9\s,"'-]+$/i],
                "font-size": [/^\d+(?:\.\d+)?(?:px|em|%|pt|rem)$/],
                "margin-left": [/^\d+(?:\.\d+)?(?:px|em|%|rem)$/],
            },
            p: {
                padding: [/^\d+(?:px|em)$/],
                margin: [/^\d+(?:px|em)$/],
            },
        },
    });

    return { __html: cleanHtml };
};

const RECEIVED_EMAIL_COLOR_VALUES = [
    /^#(0x)?[0-9a-f]+$/i,
    /^rgb\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})\s*\)$/,
    /^(blue|violet|orange|gray)$/,
];

const RECEIVED_EMAIL_ATTRIBUTES: Record<string, sanitizeHtml.AllowedAttribute[]> = {
    a: ["href", "target"],
    img: ["src", "alt", "width", "height"],
    p: ["style"],
    span: ["style"],
    div: ["style"],
    blockquote: ["style"],
    td: ["style", "colspan", "rowspan", "width", "height", "align", "valign"],
    th: ["style", "colspan", "rowspan", "width", "height", "align", "valign"],
    table: ["style", "width", "cellpadding", "cellspacing", "border"],
};

const RECEIVED_EMAIL_STYLES: Record<string, Record<string, RegExp[]>> = {
    "*": {
        color: RECEIVED_EMAIL_COLOR_VALUES,
        "background-color": RECEIVED_EMAIL_COLOR_VALUES,
        "text-align": [/^left$/, /^right$/, /^center$/],
        "font-size": [/^\d+(?:px|em|%)$/],
    },
    p: {
        "font-size": [/^\d+rem$/],
        padding: [/^\d+(?:px|em)$/],
        margin: [/^\d+(?:px|em)$/],
    },
};

const RECEIVED_EMAIL_OPTIONS: sanitizeHtml.IOptions = {
    allowedTags: [
        "br",
        "u",
        "b",
        "i",
        "em",
        "strong",
        "a",
        "img",
        "p",
        "span",
        "h1",
        "h2",
        "h3",
        "h4",
        "h5",
        "h6",
        "ul",
        "ol",
        "li",
        "div",
        "table",
        "thead",
        "tbody",
        "tfoot",
        "tr",
        "td",
        "th",
        "blockquote",
        "hr",
        "pre",
        "code",
    ],
    allowedAttributes: RECEIVED_EMAIL_ATTRIBUTES,
    allowedSchemesByTag: {
        img: ["https"],
    },
    allowedStyles: RECEIVED_EMAIL_STYLES,
};

const NEW_TAB_LINK = sanitizeHtml.simpleTransform("a", {
    target: "_blank",
    rel: "noopener noreferrer",
});

const bodyMarkupOf = (htmlBody: string): string =>
    new DOMParser().parseFromString(htmlBody, "text/html").body.innerHTML;

export const getSanitizedMarkUpFromReceivedEmailBody = (
    htmlBody: string,
): SanitizedMarkUp => ({
    __html: sanitizeHtml(bodyMarkupOf(htmlBody), RECEIVED_EMAIL_OPTIONS),
});

export const getSanitizedMarkUpFromForwardingConfirmation = (
    htmlBody: string,
): SanitizedMarkUp => ({
    __html: sanitizeHtml(bodyMarkupOf(htmlBody), {
        ...RECEIVED_EMAIL_OPTIONS,
        allowedAttributes: {
            ...RECEIVED_EMAIL_ATTRIBUTES,
            a: ["href", "target", "rel"],
        },
        allowedStyles: {
            ...RECEIVED_EMAIL_STYLES,
            "*": { ...RECEIVED_EMAIL_STYLES["*"], "white-space": [/^pre-wrap$/] },
        },
        transformTags: { a: NEW_TAB_LINK },
    }),
});
